// Lastebilkatalog: kategorier, aksler og standardverdier for nyttelast og mål.
// Tallene er standardverdier som ikke er bekreftet av Tippi ennå. Admin kan overstyre per nøkkel
// i Firestore-dokumentet settings/truckCatalog, feltet specs (se useTruckCatalog).

export const TRUCK_CATEGORIES = [
  { value: 'tipper',     label: 'Tippbil',           axles: [2, 3, 4] },
  { value: 'hooklift',   label: 'Krokløft',          axles: [2, 3, 4] },
  { value: 'crane',      label: 'Kranbil',           axles: [2, 3, 4] },
  { value: 'crane_grab', label: 'Kranbil med grabb', axles: [2, 3, 4] },
  { value: 'liftdumper', label: 'Liftdumper',        axles: [] },
];

const DEFAULT_SPECS = {
  'tipper-2':     { payloadTons: 9,  maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 800, description: 'Liten tippbil som kommer til på trange steder.' },
  'tipper-3':     { payloadTons: 13, maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 900, description: 'Vanlig tippbil for de fleste masseoppdrag.' },
  'tipper-4':     { payloadTons: 18, maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1000, description: 'Stor tippbil for store mengder og lange turer.' },
  'hooklift-2':   { payloadTons: 8,  maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1000, description: 'Krokløft med lett skiftelad.' },
  'hooklift-3':   { payloadTons: 12, maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1100, description: 'Krokløft med skiftelad, god til container og bortkjøring.' },
  'hooklift-4':   { payloadTons: 16, maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1200, description: 'Stor krokløft for tunge lass.' },
  'crane-2':      { payloadTons: 7, craneTm: 8,  maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1100, description: 'Liten kranbil, passer til sekker og pallevarer.' },
  'crane-3':      { payloadTons: 10, craneTm: 12, maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1200, description: 'Kranbil som losser selv, passer til storsekker.' },
  'crane-4':      { payloadTons: 14, craneTm: 18, maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1300, description: 'Stor kranbil med lang rekkevidde.' },
  'crane_grab-2': { payloadTons: 6, craneTm: 6,  maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1200, description: 'Liten kranbil med grabb.' },
  'crane_grab-3': { payloadTons: 9, craneTm: 10,  maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1300, description: 'Kranbil med grabb, laster og losser løsmasse selv.' },
  'crane_grab-4': { payloadTons: 13, craneTm: 15, maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 1400, description: 'Stor kranbil med grabb.' },
  liftdumper:     { payloadTons: 3,  maxWidthM: 2.2,  maxHeightM: 3.5, hourlyRate: 700, description: 'Liten bil med løftbar tipp, for små mengder og trange adkomster.' },
  trailer:        { payloadTons: 14, maxWidthM: 2.55, maxHeightM: 4, hourlyRate: 0, description: 'Henger som kjøres sammen med bilen.' },
};

export const specKey = (category, axles) => (axles ? `${category}-${axles}` : category);

const positive = v => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);

// null når kombinasjonen ikke finnes i katalogen.
export function getTruckSpec(category, axles, overrides = {}) {
  const key = specKey(category, axles);
  const base = DEFAULT_SPECS[key];
  if (!base) return null;
  const o = overrides?.[key] ?? {};
  return {
    category,
    axles: axles ?? null,
    payloadTons: positive(o.payloadTons) ?? base.payloadTons,
    maxWidthM: positive(o.maxWidthM) ?? base.maxWidthM,
    maxHeightM: positive(o.maxHeightM) ?? base.maxHeightM,
    craneTm: positive(o.craneTm) ?? base.craneTm ?? null,
    hourlyRate: positive(o.hourlyRate) ?? base.hourlyRate ?? 0,
    description: typeof o.description === 'string' && o.description.trim() ? o.description.trim() : base.description,
  };
}

export function categoryAxles(category) {
  return TRUCK_CATEGORIES.find(c => c.value === category)?.axles ?? [];
}

// Henger kan kobles på disse kategoriene. Liftdumper har ikke henger.
const TRAILER_CATEGORIES = ['tipper', 'hooklift', 'crane', 'crane_grab'];
export const supportsTrailer = category => TRAILER_CATEGORIES.includes(category);

// «Tippbil, 3-akslet», «Tippbil, 3-akslet + henger», «Liftdumper». null for ukjent kategori.
export function truckLabel(category, axles, trailer = false) {
  const cat = TRUCK_CATEGORIES.find(c => c.value === category);
  if (!cat) return null;
  const base = axles ? `${cat.label}, ${axles}-akslet` : cat.label;
  return trailer ? `${base} + henger` : base;
}

export const formatNo = n => String(n).replace('.', ',');

export function truckSpecLine(spec) {
  return `Nyttelast ca. ${formatNo(spec.payloadTons)} t · Bredde ${formatNo(spec.maxWidthM)} m · Høyde ${formatNo(spec.maxHeightM)} m`;
}

// Plassholdere til kollegaen har bekreftet størrelsene og vekten.
export const CONTAINER_SIZES = [5, 7, 10, 15];
export const BAG_TONS = 1;
export const isCraneCategory = category => category === 'crane' || category === 'crane_grab';
